import type { MetaStandardEventName } from "@vetpets/event-contracts"

import type { MetaPixelAdvancedMatchingData } from "./meta-advanced-matching"

type PixelCustomData = Record<string, unknown>
type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[][]
  push?: (...args: unknown[]) => void
  loaded?: boolean
  version?: string
  disablePushState?: boolean
}

declare global {
  interface Window {
    fbq?: Fbq
    _fbq?: Fbq
  }
}

export const META_PIXEL_SCRIPT_ID = "vetpets-meta-pixel-script"
const META_PIXEL_SCRIPT_SRC = "https://connect.facebook.net/en_US/fbevents.js"
const META_PURCHASE_SESSION_KEY_PREFIX = "vetpets-meta:v1:browser-purchase-fired:"

const META_NAMESPACED_EVENT_ID = /^vetpets-meta:v1:(PageView|ViewContent|Search|AddToCart|InitiateCheckout|AddPaymentInfo|Purchase):([A-Za-z0-9][A-Za-z0-9._:-]{0,255})$/
const META_UUID_EVENT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function isValidMetaPixelEventId(
  eventName: MetaStandardEventName,
  eventId: string
): boolean {
  if (typeof eventId !== "string" || !eventId) return false
  const namespaced = META_NAMESPACED_EVENT_ID.exec(eventId)
  if (namespaced) return namespaced[1] === eventName
  return META_UUID_EVENT_ID.test(eventId)
}

type PendingPixelEvent = {
  eventName: MetaStandardEventName
  customData: PixelCustomData
  eventId: string
}

const pendingPixelEvents: PendingPixelEvent[] = []
let initializedPixelId: string | null = null
let initializedAdvancedMatchingFingerprint = ""
let browserPixelTrackingDisabled = false

const ADVANCED_MATCHING_KEYS = new Set(["em", "ph", "fn", "ln", "ct", "st", "zp", "country"])

function safeAdvancedMatchingData(value?: MetaPixelAdvancedMatchingData): MetaPixelAdvancedMatchingData {
  const output: MetaPixelAdvancedMatchingData = {}
  if (!value || typeof value !== "object" || Array.isArray(value)) return output
  for (const [key, raw] of Object.entries(value)) {
    if (!ADVANCED_MATCHING_KEYS.has(key) || typeof raw !== "string") continue
    const normalized = raw.trim()
    if (!normalized || normalized.length > 256) continue
    ;(output as Record<string, string>)[key] = normalized
  }
  return output
}

function advancedMatchingFingerprint(value: MetaPixelAdvancedMatchingData): string {
  return JSON.stringify(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)))
}

function purchaseSessionKey(eventId: string): string {
  return `${META_PURCHASE_SESSION_KEY_PREFIX}${eventId}`
}

function purchaseAlreadyTracked(eventName: MetaStandardEventName, eventId: string): boolean {
  if (eventName !== "Purchase") return false
  try {
    return window.sessionStorage.getItem(purchaseSessionKey(eventId)) === "1"
  } catch {
    return false
  }
}

function markPurchaseTracked(eventName: MetaStandardEventName, eventId: string): void {
  if (eventName !== "Purchase") return
  try {
    window.sessionStorage.setItem(purchaseSessionKey(eventId), "1")
  } catch {
    // Storage availability must never break best-effort Pixel delivery.
  }
}

function sendPixelEvent(event: PendingPixelEvent): void {
  if (!window.fbq || purchaseAlreadyTracked(event.eventName, event.eventId)) return
  window.fbq("track", event.eventName, event.customData, { eventID: event.eventId })
  markPurchaseTracked(event.eventName, event.eventId)
}

function ensureFbq(): Fbq {
  if (window.fbq) return window.fbq

  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, args)
    } else {
      ;(fbq.queue ??= []).push(args)
    }
  } as Fbq
  fbq.push = fbq
  fbq.loaded = true
  fbq.version = "2.0"
  fbq.queue = []
  window.fbq = fbq
  window._fbq = fbq
  return fbq
}

function injectPixelScriptOnce(): void {
  if (document.getElementById(META_PIXEL_SCRIPT_ID)) return
  const script = document.createElement("script")
  script.id = META_PIXEL_SCRIPT_ID
  script.async = true
  script.src = META_PIXEL_SCRIPT_SRC
  const firstScript = document.getElementsByTagName("script")[0]
  if (firstScript?.parentNode) firstScript.parentNode.insertBefore(script, firstScript)
  else document.head.appendChild(script)
}

export function flushPendingPixelEvents(): void {
  if (!initializedPixelId || !window.fbq) return
  while (pendingPixelEvents.length) {
    sendPixelEvent(pendingPixelEvents.shift()!)
  }
}

export function discardPendingPixelEvents(): void {
  pendingPixelEvents.length = 0
}

export function disableMetaPixelTracking(): void {
  browserPixelTrackingDisabled = true
  discardPendingPixelEvents()
}

export function initializeMetaPixel(
  pixelId: string,
  advancedMatchingData?: MetaPixelAdvancedMatchingData
): void {
  const normalizedPixelId = String(pixelId ?? "").trim()
  if (!/^\d{5,32}$/.test(normalizedPixelId)) return
  browserPixelTrackingDisabled = false
  if (initializedPixelId && initializedPixelId !== normalizedPixelId) return

  const matching = safeAdvancedMatchingData(advancedMatchingData)
  const fingerprint = advancedMatchingFingerprint(matching)
  const fbq = ensureFbq()

  if (initializedPixelId === normalizedPixelId) {
    if (fingerprint === initializedAdvancedMatchingFingerprint) return
    fbq("init", normalizedPixelId, matching)
    initializedAdvancedMatchingFingerprint = fingerprint
    return
  }

  fbq("set", "autoConfig", false, normalizedPixelId)
  fbq.disablePushState = true
  if (Object.keys(matching).length) fbq("init", normalizedPixelId, matching)
  else fbq("init", normalizedPixelId)
  initializedPixelId = normalizedPixelId
  initializedAdvancedMatchingFingerprint = fingerprint
  injectPixelScriptOnce()
  flushPendingPixelEvents()
}

export function trackPixelEvent(
  eventName: MetaStandardEventName,
  customData: PixelCustomData,
  eventId: string
): void {
  if (!isValidMetaPixelEventId(eventName, eventId)) return
  if (browserPixelTrackingDisabled) return
  if (!initializedPixelId || !window.fbq) {
    pendingPixelEvents.push({ eventName, customData, eventId })
    return
  }
  sendPixelEvent({ eventName, customData, eventId })
}
